import React, { useEffect, useMemo, useState, useCallback } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import './InstrumentSearch.css';
import { ReactComponent as OandaIcon } from '../../../assets/icons/oanda_icon.svg';
import { ReactComponent as BinanceIcon } from '../../../assets/icons/binance_icon.svg';
import { ReactComponent as YahooIcon } from '../../../assets/icons/yahoo_icon.svg';
import { useCryptoEnabled, useStocksEnabled } from '../../../context/FeatureFlagContext';

const RECENT_INSTRUMENTS_KEY = 'nt_recent_instruments';
const MAX_RECENT_INSTRUMENTS = 5;

const TAB_OPTIONS = [
  { id: 'ALL', label: 'All' },
  { id: 'FOREX', label: 'Forex' },
  { id: 'CRYPTO', label: 'Crypto' },
  { id: 'INDICES', label: 'Indices' },
  { id: 'STOCKS', label: 'Stocks' }
];

const CATEGORY_LABELS = {
  FOREX: 'Forex',
  CRYPTO: 'Crypto',
  INDICES: 'Indices',
  STOCKS: 'Stocks'
};

const getProviderIcon = (provider) => {
  switch (provider) {
    case 'OANDA':
      return <OandaIcon />;
    case 'BINANCE':
      return <BinanceIcon />;
    case 'YAHOO':
      return <YahooIcon />;
    default:
      return null;
  }
};

const buildStockLabel = (option) => {
  const stock = option.data || {};
  return stock.shortname || stock.longname || option.label || option.value;
};

const InstrumentSearch = ({
  instruments,
  autoCompleteOptions = [],
  selectedInstrument,
  selectedCategory,
  onSelectInstrument,
  onSearch,
  onSearchActiveChange,
  isDarkMode,
  variant = 'mobile',
  shouldHighlight = false,
  className = '',
  defaultCategory = 'ALL'
}) => {
  const cryptoEnabled = useCryptoEnabled();
  const stocksEnabled = useStocksEnabled();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const initialTab = TAB_OPTIONS.some(tab => tab.id === selectedCategory)
    ? selectedCategory
    : defaultCategory;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [query, setQuery] = useState('');
  const [recentInstruments, setRecentInstruments] = useState([]);

  // Load recent instruments from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_INSTRUMENTS_KEY);
      if (stored) {
        setRecentInstruments(JSON.parse(stored));
      }
    } catch (e) {
      // Ignore parse errors
    }
  }, []);

  // Save instrument to recent list
  const saveToRecent = useCallback((entry) => {
    setRecentInstruments((prev) => {
      // Remove if already exists
      const filtered = prev.filter(
        (r) => !(r.symbol === entry.symbol && r.category === entry.category)
      );
      // Add to front
      const updated = [
        { symbol: entry.symbol, category: entry.category, label: entry.label, provider: entry.provider },
        ...filtered
      ].slice(0, MAX_RECENT_INSTRUMENTS);
      // Persist
      try {
        localStorage.setItem(RECENT_INSTRUMENTS_KEY, JSON.stringify(updated));
      } catch (e) {
        // Ignore storage errors
      }
      return updated;
    });
  }, []);

  // Filter tabs based on feature flags
  const availableTabs = useMemo(() => {
    return TAB_OPTIONS.filter(tab => {
      if (tab.id === 'CRYPTO' && !cryptoEnabled) return false;
      if (tab.id === 'STOCKS' && !stocksEnabled) return false;
      return true;
    });
  }, [cryptoEnabled, stocksEnabled]);

  useEffect(() => {
    if (onSearchActiveChange) {
      onSearchActiveChange(isSearchOpen);
    }
  }, [isSearchOpen, onSearchActiveChange]);

  useEffect(() => {
    if (onSearch) {
      onSearch(query);
    }
  }, [query, onSearch]);

  useEffect(() => {
    const nextTab = TAB_OPTIONS.some(tab => tab.id === selectedCategory)
      ? selectedCategory
      : defaultCategory;
    setActiveTab(nextTab || 'ALL');
  }, [selectedCategory, defaultCategory]);

  const entries = useMemo(() => {
    const results = [];
    const seenKeys = new Set();

    const pushEntry = (entry) => {
      if (!entry.symbol) {
        return;
      }
      const dedupeKey = `${entry.category}:${entry.symbol}`;
      if (seenKeys.has(dedupeKey)) {
        return;
      }
      seenKeys.add(dedupeKey);
      results.push(entry);
    };

    Object.entries(instruments || {}).forEach(([category, categoryItems]) => {
      if (category === 'STOCKS') {
        return; // handled separately to avoid duplicates with stockMap
      }
      Object.entries(categoryItems || {}).forEach(([symbol, details]) => {
        pushEntry({
          category,
          symbol,
          label: details.label || symbol,
          provider: details.provider,
          description: details.label || symbol,
          source: 'preset'
        });
      });
    });

    const stockMap = new Map();
    autoCompleteOptions.forEach((option) => {
      stockMap.set(option.value, {
        category: 'STOCKS',
        symbol: option.value,
        label: buildStockLabel(option),
        provider: option.provider || option.data?.provider || 'YAHOO',
        description: option.data?.exchDisp || option.data?.exchange || '',
        source: 'api'
      });
    });

    Object.entries(instruments?.STOCKS || {}).forEach(([symbol, details]) => {
      if (!stockMap.has(symbol)) {
        stockMap.set(symbol, {
          category: 'STOCKS',
          symbol,
          label: details.label || symbol,
          provider: details.provider || 'YAHOO',
          description: details.label || symbol,
          source: 'preset'
        });
      }
    });

    results.push(...stockMap.values());

    return results;
  }, [instruments, autoCompleteOptions]);

  const filteredEntries = useMemo(() => {
    const normalizedQuery = query.trim().toUpperCase();
    const normalizedTokens = normalizedQuery
      ? normalizedQuery.replace(/\s+/g, ' ').split(' ').filter(Boolean)
      : [];
    const normalizedQueryNoSymbols = normalizedQuery.replace(/[^A-Z0-9]/g, '');

    const matchesQuery = (entry) => {
      if (!normalizedQuery) {
        return true;
      }
      const rawTarget = `${entry.symbol} ${entry.label || ''} ${entry.description || ''}`.toUpperCase();
      const targetNoSymbols = rawTarget.replace(/[^A-Z0-9]/g, '');

      const tokenMatch = normalizedTokens.every(token => rawTarget.includes(token));
      const concatenatedMatch = normalizedQueryNoSymbols && targetNoSymbols.includes(normalizedQueryNoSymbols);

      return tokenMatch || concatenatedMatch;
    };

    return entries
      .filter((entry) => {
        // Filter by tab
        if (activeTab !== 'ALL' && entry.category !== activeTab) return false;
        // Filter out disabled asset types
        if (entry.category === 'CRYPTO' && !cryptoEnabled) return false;
        if (entry.category === 'STOCKS' && !stocksEnabled) return false;
        return true;
      })
      .filter(matchesQuery)
      .sort((a, b) => a.symbol.localeCompare(b.symbol));
  }, [entries, activeTab, query, cryptoEnabled, stocksEnabled]);

  const selectedLabel = selectedInstrument
    ? instruments?.[selectedCategory]?.[selectedInstrument]?.label || selectedInstrument.replace('_', '/')
    : 'Search instruments';

  const handleClose = () => {
    setIsSearchOpen(false);
    setQuery('');
  };

  const handleSelect = (entry) => {
    if (onSelectInstrument) {
      onSelectInstrument(entry.symbol, entry.category);
    }
    saveToRecent(entry);
    setQuery(entry.symbol);
    setIsSearchOpen(false);
  };

  const handleClearQuery = () => {
    setQuery('');
  };

  const containerClassName = [
    'instrument-search',
    `instrument-search--${variant}`,
    className
  ].filter(Boolean).join(' ');

  const triggerClassName = [
    'instrument-search-trigger',
    variant === 'desktop' && 'instrument-search-trigger--desktop',
    shouldHighlight && 'instrument-search-trigger-highlight'
  ].filter(Boolean).join(' ');

  const overlayClassName = [
    'instrument-search-overlay',
    variant === 'desktop' && 'instrument-search-overlay--desktop',
    isDarkMode && 'dark-mode'
  ].filter(Boolean).join(' ');

  const panelClassName = [
    'instrument-search-panel',
    variant === 'desktop' && 'instrument-search-panel--desktop'
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClassName}>
      <button
        type="button"
        className={triggerClassName}
        onClick={() => setIsSearchOpen(true)}
        aria-label="Search instruments"
      >
        <SearchIcon fontSize="small" />
        <span className="instrument-search-trigger-text">
          {selectedLabel}
        </span>
      </button>

      {isSearchOpen && (
        <div className={overlayClassName}>
          <div className={panelClassName}>
            <div className="instrument-search-header">
              <div className="instrument-search-input">
                <SearchIcon fontSize="small" />
                <input
                  type="text"
                  placeholder="Search symbols"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  autoFocus
                />
              </div>
              {query && (
                <button
                  type="button"
                  className="instrument-search-clear"
                  onClick={handleClearQuery}
                  aria-label="Clear search input"
                >
                  Clear
                </button>
              )}
              <button
                type="button"
                className="instrument-search-close"
                onClick={handleClose}
                aria-label="Close search"
              >
                <CloseIcon fontSize="small" />
              </button>
            </div>
            <div className="instrument-search-tabs" role="tablist" aria-label="Search categories">
              {availableTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`instrument-search-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="instrument-search-results">
              {/* Recent instruments section - show when no query */}
              {!query && recentInstruments.length > 0 && (
                <div className="instrument-search-section">
                  <div className="instrument-search-section-header">
                    <HistoryIcon fontSize="small" />
                    <span>Recent</span>
                  </div>
                  {recentInstruments.map((recent) => (
                    <button
                      key={`recent-${recent.category}-${recent.symbol}`}
                      type="button"
                      className={`instrument-search-item instrument-search-item--recent ${
                        recent.symbol === selectedInstrument && recent.category === selectedCategory
                          ? 'selected'
                          : ''
                      }`}
                      onClick={() => handleSelect(recent)}
                    >
                      <span className="instrument-search-item-icon">
                        {getProviderIcon(recent.provider)}
                      </span>
                      <span className="instrument-search-item-content">
                        <span className="instrument-search-item-title">
                          {recent.symbol}
                        </span>
                        <span className="instrument-search-item-subtitle">
                          {recent.label || recent.symbol}
                        </span>
                      </span>
                      <span className="instrument-search-item-meta">
                        {CATEGORY_LABELS[recent.category] || recent.category}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {filteredEntries.length === 0 ? (
                <div className="instrument-search-empty">
                  No matching instruments.
                </div>
              ) : (
                filteredEntries.map((entry) => (
                  <button
                    key={`${entry.category}-${entry.symbol}-${entry.source}`}
                    type="button"
                    className={`instrument-search-item ${entry.symbol === selectedInstrument && entry.category === selectedCategory
                        ? 'selected'
                        : ''
                      }`}
                    onClick={() => handleSelect(entry)}
                  >
                    <span className="instrument-search-item-icon">
                      {getProviderIcon(entry.provider)}
                    </span>
                    <span className="instrument-search-item-content">
                      <span className="instrument-search-item-title">
                        {entry.symbol}
                      </span>
                      <span className="instrument-search-item-subtitle">
                        {entry.label}
                      </span>
                    </span>
                    <span className="instrument-search-item-meta">
                      {CATEGORY_LABELS[entry.category] || entry.category}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstrumentSearch;
